import { useState } from "react";

import { getErrorMessage } from "@/core/services";
import { useAsyncQuery } from "@/ds";
import { previaProjecaoTransferencia } from "@/modules/financeiro-obras";
import type {
  DecisaoProjecaoTransferencia,
  PlanejamentoControle,
  PreviaProjecaoTransferencia,
} from "@/modules/financeiro-obras";
import { usePlanejamentoControleDados } from "../../../../hooks/use-planejamento-dados";
import {
  PREVIA_ERRO,
  PREVIA_PERMISSAO,
} from "../ajustar-projecao-transferencia-dialog/ajustar-projecao-transferencia-dialog.constants";

interface Params {
  obraId: string;
  escopoId: string;
  tipoObraCodigo: string;
  destinoId: string | null;
  centroCustoId: string;
}

function totalDoTipo(planejamento: PlanejamentoControle | null, codigo: string): number | null {
  const tipos = planejamento?.centrosCusto.flatMap((centro) => centro.tiposObra) ?? [];
  return tipos.find((tipo) => tipo.tipoObra.codigo === codigo)?.totais.outlook.total ?? null;
}

export function useRevisaoProjecaoTransferencia({
  obraId,
  escopoId,
  tipoObraCodigo,
  destinoId,
  centroCustoId,
}: Params) {
  const chave = destinoId ? `${destinoId}|${centroCustoId}` : null;
  const [alcancada, setAlcancada] = useState<string | null>(null);
  const [guardada, setGuardada] = useState<{
    chave: string;
    valor: DecisaoProjecaoTransferencia;
  } | null>(null);
  const decisao = guardada && guardada.chave === chave ? guardada.valor : null;
  const ativa = chave !== null && alcancada === chave;

  const previa = useAsyncQuery<PreviaProjecaoTransferencia | null>({
    fetcher: (signal) =>
      previaProjecaoTransferencia(
        obraId,
        escopoId,
        {
          obraDestinoId: destinoId ?? "",
          ...(centroCustoId ? { centroCustoDestinoId: centroCustoId } : {}),
          ...(decisao ? { projecao: decisao } : {}),
        },
        { signal },
      ),
    queryKey: `previa-transferencia/${obraId}/${escopoId}/${chave}/${JSON.stringify(decisao)}`,
    initialData: null,
    enabled: ativa,
    keepPrevious: true,
    forbiddenMessage: PREVIA_PERMISSAO,
    errorMessage: PREVIA_ERRO,
    resolveErrorMessage: getErrorMessage,
  });
  const planejamentoDestino = usePlanejamentoControleDados(destinoId ?? "", ativa);

  const falhou = previa.status === "error" || previa.status === "forbidden";
  let estado: "inativo" | "carregando" | "erro" | "pronto" = "pronto";
  if (!ativa) estado = "inativo";
  else if (falhou) estado = "erro";
  else if (previa.status === "loading" || planejamentoDestino.status === "loading") {
    estado = "carregando";
  }

  return {
    estado,
    errorMessage: falhou ? previa.errorMessage : null,
    previa: previa.data,
    totalAtualTipo: totalDoTipo(planejamentoDestino.data, tipoObraCodigo),
    decisao,
    iniciar: () => setAlcancada(chave),
    aplicarDecisao: (valor: DecisaoProjecaoTransferencia | null) =>
      setGuardada(chave && valor ? { chave, valor } : null),
    recarregar: () => {
      previa.reload();
      planejamentoDestino.reload();
    },
  };
}
